import {
    type LearnTask,
    type MentionPillData,
} from '@apps/web/features/learn_phoebe/model/learn_content';

export const COMPOSE_START_DELAY_MS = 400;
export const COMPOSE_CHAR_MS = 28;
export const COMPOSE_DROPDOWN_OPEN_DELAY_MS = 350;
export const COMPOSE_DROPDOWN_HOLD_MS = 900;
export const COMPOSE_SEND_DELAY_MS = 600;

// The composer acts out the whole ask before the conversation starts: type,
// pause on @ for the picker, commit the pill, then send. Each step below is
// one atomic beat of that sequence.
export type ComposeStep =
    | { readonly kind: 'char'; readonly char: string }
    | { readonly kind: 'query-char'; readonly char: string }
    | { readonly kind: 'dropdown'; readonly pill: MentionPillData }
    | { readonly kind: 'commit-pill'; readonly pill: MentionPillData }
    | { readonly kind: 'send' };

export function buildComposeSteps(task: LearnTask): ReadonlyArray<ComposeStep> {
    const firstTurn = task.turns[0];
    if (firstTurn === undefined || firstTurn.kind !== 'user') {
        return [{ kind: 'send' }];
    }
    const steps: Array<ComposeStep> = [];
    for (const segment of firstTurn.segments) {
        if ('text' in segment) {
            for (const char of segment.text) {
                steps.push({ kind: 'char', char });
            }
        } else {
            const query =
                segment.pill.query ?? `@${segment.pill.label.slice(0, 2)}`;
            for (const char of query) {
                steps.push({ kind: 'query-char', char });
            }
            steps.push({ kind: 'dropdown', pill: segment.pill });
            steps.push({ kind: 'commit-pill', pill: segment.pill });
        }
    }
    steps.push({ kind: 'send' });
    return steps;
}

// Delay before a step happens, so the picker lingers and the send breathes.
export function composeStepDelayMs(step: ComposeStep): number {
    switch (step.kind) {
        case 'char':
        case 'query-char':
            return COMPOSE_CHAR_MS;
        case 'dropdown':
            return COMPOSE_DROPDOWN_OPEN_DELAY_MS;
        case 'commit-pill':
            return COMPOSE_DROPDOWN_HOLD_MS;
        case 'send':
            return COMPOSE_SEND_DELAY_MS;
        default:
            step satisfies never;
            return 0;
    }
}

export type ComposerView = {
    readonly typed: ReadonlyArray<
        { readonly text: string } | { readonly pill: MentionPillData }
    >;
    readonly pendingQuery: string;
    readonly dropdownPill: MentionPillData | null;
    readonly hasSent: boolean;
};

export function buildComposerView(
    steps: ReadonlyArray<ComposeStep>,
    consumedCount: number,
): ComposerView {
    const typed: Array<{ text: string } | { pill: MentionPillData }> = [];
    let pendingQuery = '';
    let dropdownPill: MentionPillData | null = null;
    let hasSent = false;
    for (const step of steps.slice(0, consumedCount)) {
        switch (step.kind) {
            case 'char': {
                const last = typed[typed.length - 1];
                if (last !== undefined && 'text' in last) {
                    last.text += step.char;
                } else {
                    typed.push({ text: step.char });
                }
                break;
            }
            case 'query-char':
                pendingQuery += step.char;
                break;
            case 'dropdown':
                dropdownPill = step.pill;
                break;
            case 'commit-pill':
                typed.push({ pill: step.pill });
                pendingQuery = '';
                dropdownPill = null;
                break;
            case 'send':
                hasSent = true;
                break;
            default:
                step satisfies never;
                break;
        }
    }
    return { typed, pendingQuery, dropdownPill, hasSent };
}
